import { Platform } from 'react-native';
import { inCallManagerMedia } from './callAudioNativeState';
import {
  webRtcAudioSessionDidActivate,
  webRtcAudioSessionDidDeactivate,
} from './rtcAudioSessionBridge';
import { voiceCallLog } from './voiceCallDebug';

/**
 * Единственный владелец AVAudioSession на время звонка (стори 16.2 / FN-2.2).
 *
 * <p><b>Проблема.</b> Аудиосессию iOS одновременно настраивали четверо, и никто
 * не знал о существовании остальных:
 *
 * <ol>
 *   <li><b>CallKit</b> (через RNCallKeep) — активирует сессию сам, когда
 *       пользователь принимает звонок, и повторно её конфигурирует в
 *       {@code configureAudioSession}: категория {@code PlayAndRecord},
 *       режим <b>Default</b>, {@code setActive:YES};</li>
 *   <li><b>InCallManager</b> — {@code start()} ставит режим
 *       {@code VoiceChat}/{@code VideoChat} и делает {@code setActive:YES},
 *       {@code stop()} — <b>{@code setActive:NO}</b>;</li>
 *   <li><b>WebRTC</b> (форк LiveKit) — поднимает свой VoIP audio unit и
 *       активирует сессию сам, когда появляется аудиодорожка;</li>
 *   <li><b>react-native-sound</b> — рингтон и гудки, категория {@code Playback}.</li>
 * </ol>
 *
 * <p><b>Чем это кончалось.</b> При приёме звонка с заблокированного экрана
 * порядок был такой: CallKit активировал сессию → мы вызывали
 * {@code applyVoiceCallAudioModeNative({resetForCapture:true})} → тот делал
 * {@code InCallManager.stop()}, то есть <b>{@code setActive:NO}</b> на сессии,
 * которую CallKit только что выдал, → и следом {@code start()} с
 * {@code setActive:YES} из фонового приложения. Apple прямо требует обратного:
 * при CallKit приложение <b>только конфигурирует</b> сессию, а активирует её
 * исключительно CallKit. Отсюда «звонок принят, соединение есть, звука нет».
 *
 * <p><b>Правило.</b> Пока CallKit держит сессию активной, никто, кроме него,
 * не трогает активацию. Нам остаётся маршрут (динамик/наушник) — это
 * {@code overrideOutputAudioPort}, он не про владение и работает всегда.
 *
 * <p><b>Когда начинается владение.</b> Нельзя ждать
 * {@code didActivateAudioSession}: CallKit шлёт его <b>после</b> того, как
 * выполнено действие «ответить», а микрофон мы открываем прямо в обработчике
 * ответа — то есть раньше. Ждать активации значило бы включить защиту уже
 * после того, как вредный {@code setActive:NO} случился. Поэтому владение
 * начинается с момента, когда мы узнали о живом звонке CallKit (ответ на
 * входящий или экран звонка с {@code callKitUUID}), а событие активации его
 * лишь подтверждает и дополнительно сообщает WebRTC.
 */

type CallAudioOwner = 'app' | 'callkit';

/** Событие в ленте для диагностики (стори 16.3). */
type AudioSessionEvent = {
  /** Миллисекунды от эпохи — в отчёте показываем разницу, а не абсолют. */
  at: number;
  name: string;
};

/** Лента коротка намеренно: это хвост последнего звонка, а не журнал. */
const MAX_EVENTS = 24;

/** Живой звонок в CallKit: от «ответить» (или экрана с callKitUUID) до конца. */
let callKitCallLive = false;
/** Сессия активирована самим CallKit (между didActivate и didDeactivate). */
let callKitAudioLive = false;
let events: AudioSessionEvent[] = [];

function note(name: string): void {
  events.push({ at: Date.now(), name });
  if (events.length > MAX_EVENTS) {
    events = events.slice(-MAX_EVENTS);
  }
}

/**
 * CallKit активировал сессию.
 *
 * <p>Помимо смены владельца обязательно сообщаем об этом WebRTC: у
 * `RTCAudioSession` свой счётчик активаций, и без этого вызова он считает
 * сессию неактивной. Последствия двойные — WebRTC пытается активировать её
 * ещё раз (из фона это запрещено) и, что хуже, при завершении своей работы
 * делает {@code setActive:NO} по счётчику, которого не занимал, обрывая звук
 * посреди разговора.
 *
 * <p><b>Только iOS.</b> Событие с тем же именем шлёт и Android: RNCallKeep
 * поднимает {@code RNCallKeepDidActivateAudioSession} из
 * {@code VoiceConnection.onAnswer}. Но пары ему там нет — деактивацию
 * ConnectionService не сообщает вовсе, — так что флаг вставал и не снимался, а
 * вместе с ним на весь разговор отключался {@link applyVoiceCallAudioModeNative}
 * → {@code InCallManager.start()}. Без него AudioManager не переходит в
 * {@code MODE_IN_COMMUNICATION}, и маршрут (наушник/динамик) на Android не
 * работает вообще. CallKit — понятие iOS, здесь это и закреплено.
 */
export function noteCallKitAudioActivated(): void {
  if (Platform.OS !== 'ios') return;
  callKitAudioLive = true;
  note('callkit_activated');
  voiceCallLog('audio session: CallKit activated', {});
  const signal = webRtcAudioSessionDidActivate();
  note(signal === 'ok' ? 'webrtc_did_activate' : `webrtc_did_activate_${signal}`);
}

/**
 * CallKit отпустил сессию. Владение при этом снимается только в части
 * активации: сам звонок может быть ещё жив (короткое прерывание), и решение
 * «звонок кончился» принимает {@link noteCallKitCallEnded}.
 */
export function noteCallKitAudioDeactivated(): void {
  if (Platform.OS !== 'ios') return;
  callKitAudioLive = false;
  note('callkit_deactivated');
  voiceCallLog('audio session: CallKit deactivated', {});
  const signal = webRtcAudioSessionDidDeactivate();
  note(signal === 'ok' ? 'webrtc_did_deactivate' : `webrtc_did_deactivate_${signal}`);
}

/**
 * Провайдер CallKit сброшен (перезапуск, сбой). Ни одного живого звонка не
 * осталось, и ждать `didDeactivateAudioSession` неоткуда — иначе владение
 * зависло бы на CallKit навсегда, и следующий звонок остался бы без звука.
 */
export function resetCallKitAudioOwnership(): void {
  if (callKitCallLive || callKitAudioLive) {
    note('callkit_reset_while_owning');
  }
  callKitCallLive = false;
  callKitAudioLive = false;
}

/**
 * У CallKit появился живой звонок: пользователь ответил через системный экран,
 * либо экран звонка получил `callKitUUID`. С этого момента активацией сессии
 * распоряжается только CallKit.
 *
 * <p>Именно здесь, а не на `didActivateAudioSession`, потому что активация
 * приезжает уже после того, как мы открыли микрофон.
 */
export function noteCallKitCallStarted(): void {
  if (Platform.OS !== 'ios') return;
  if (callKitCallLive) return;
  callKitCallLive = true;
  note('callkit_call_started');
  voiceCallLog('audio session: CallKit owns the call', {});
}

/** Звонок CallKit завершён: владение возвращается приложению. */
export function noteCallKitCallEnded(): void {
  if (!callKitCallLive && !callKitAudioLive) return;
  callKitCallLive = false;
  callKitAudioLive = false;
  note('callkit_call_ended');
  voiceCallLog('audio session: CallKit released the call', {});
}

/**
 * Занята ли аудиосессия разговором прямо сейчас.
 *
 * <p>Нужно тем, кто играет короткие звуки: на iOS `Sound.setCategory` — это
 * немедленный `[AVAudioSession setCategory:]` на <b>общей</b> сессии, той
 * самой, на которой идёт разговор. Служебная отбивка «соединились» просила
 * `Ambient`, а у `Ambient` нет ни входа, ни разговорного динамика — маршрут
 * живого звонка уезжал в громкий динамик, а захват оставался без сессии.
 * Пока звонок идёт, категорию не трогает никто.
 *
 * <p>Владением считается любая из двух половин: запущенный нативный менеджер
 * (обычный звонок) или живой звонок CallKit (там `start()` пропущен намеренно,
 * см. {@link callKitOwnsAudioSession}).
 */
export function callAudioSessionBusy(): boolean {
  return inCallManagerMedia() !== null || callKitOwnsAudioSession();
}

export function callAudioOwner(): CallAudioOwner {
  return callKitOwnsAudioSession() ? 'callkit' : 'app';
}

/**
 * Владеет ли сессией CallKit прямо сейчас.
 *
 * <p>Живого звонка достаточно: между «ответить» и активацией есть окно, в
 * котором сессия уже принадлежит CallKit, хотя событие активации ещё не
 * пришло. Ровно в это окно и попадал захват микрофона.
 */
export function callKitOwnsAudioSession(): boolean {
  return callKitCallLive || callKitAudioLive;
}

/** Отметить в ленте то, что сделал сам звонок (для диагностики). */
export function noteCallAudioEvent(name: string): void {
  note(name);
}

/**
 * Блок для отчёта «Скопировать диагностику» (стори 16.3).
 *
 * <p>Без него проверка на устройстве упирается в «звук есть / звука нет», а
 * различить «CallKit не активировал сессию» и «активировал, но мы её выбили»
 * невозможно — при том что чинятся они по-разному.
 */
export function describeCallAudioSession(): string {
  if (events.length === 0) {
    return `Аудиосессия: владелец ${callAudioOwner()}, событий нет`;
  }
  const first = events[0].at;
  const timeline = events
    .map((e) => `+${((e.at - first) / 1000).toFixed(1)}с ${e.name}`)
    .join('\n  ');
  return `Аудиосессия: владелец ${callAudioOwner()}\n  ${timeline}`;
}

/** Только для тестов: лента и владение — модульное состояние. */
export function resetCallAudioSessionForTests(): void {
  callKitCallLive = false;
  callKitAudioLive = false;
  events = [];
}
